import { useState } from "react";
import { Mail, MapPin, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Reveal, Field, inputCls } from "@/components/ui-bits";

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", {
        full_name: form.full_name || "", email: form.email || "", phone: form.phone || "",
        organization: form.organization || "", country: form.country || "",
        subject: form.subject || "", message: form.message || "",
      });
      toast.success(t("form.success"));
      setForm({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="hero-grain relative overflow-hidden bg-navy-950 py-24 lg:py-28">
        <img src={IMAGES.cityNight} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/50 to-navy-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">{t("nav.contact")}</p>
            <h1 className="max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl">{t("contact.title")}</h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">{t("contact.sub")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[380px_1fr] lg:px-12">
          <Reveal className="h-fit rounded-lg bg-navy p-8 text-white">
            <h2 className="font-display text-2xl font-semibold">Stratedge UK</h2>
            <div className="mt-6 space-y-5 text-sm">
              <a href="mailto:info@stratedgeuk.com" data-testid="contact-email-link" className="flex items-center gap-3 text-gold-light hover:text-gold">
                <Mail className="h-5 w-5 text-gold" /> info@stratedgeuk.com
              </a>
              <p className="flex items-center gap-3 text-slate-300">
                <MapPin className="h-5 w-5 text-gold" /> {t("contact.hq")}
              </p>
              <p className="flex items-center gap-3 text-slate-300">
                <Globe2 className="h-5 w-5 text-gold" /> UK · Europe · Middle East
              </p>
            </div>
            <div className="gold-hairline my-7 opacity-40" />
            <p className="text-sm leading-relaxed text-slate-400">{t("cta.sub")}</p>
          </Reveal>

          <Reveal delay={0.1} className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
            <h2 className="font-display text-2xl font-semibold text-navy">{t("contact.formTitle")}</h2>
            <form onSubmit={submit} className="mt-6 grid gap-5 sm:grid-cols-2" data-testid="contact-form">
              <Field label={t("form.fullName")} required><input required className={inputCls} value={form.full_name || ""} onChange={set("full_name")} data-testid="contact-name" /></Field>
              <Field label={t("form.email")} required><input required type="email" className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="contact-email" /></Field>
              <Field label={t("form.phone")}><input className={inputCls} value={form.phone || ""} onChange={set("phone")} data-testid="contact-phone" /></Field>
              <Field label={t("form.organization")}><input className={inputCls} value={form.organization || ""} onChange={set("organization")} data-testid="contact-org" /></Field>
              <Field label={t("form.country")}><input className={inputCls} value={form.country || ""} onChange={set("country")} data-testid="contact-country" /></Field>
              <Field label={t("form.subject")}><input className={inputCls} value={form.subject || ""} onChange={set("subject")} data-testid="contact-subject" /></Field>
              <div className="sm:col-span-2">
                <Field label={t("form.message")} required><textarea required rows={5} className={inputCls} value={form.message || ""} onChange={set("message")} data-testid="contact-message" /></Field>
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={sending} data-testid="contact-submit-button"
                  className="rounded-full bg-gold px-10 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-gold-hover disabled:opacity-60">
                  {sending ? t("form.sending") : t("form.submit")}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
